// Block C: the Aidey Chat page (design/business/mockups/chat.html, "Full chat page"), on the v2
// kit and tokens (chat.css). Desktop and tablet: one card, the history on the left (search,
// Today / This week / Earlier, projects kept), the conversation on the right. Phone (addendum 8):
// full screen, the history is its own view behind a "Chats" button, and the composer sits above
// the keyboard and the home indicator. Renders in both workspaces.
import React, { useState, useCallback, useRef, useEffect } from 'react';
import SuperAideyChat from '../SuperAideyChat';
import HelpButton from '../../tours/HelpButton';
import useAutoTour from '../../tours/useAutoTour';
import { api } from '../../api/client';
import { loadSessions, loadSession, deleteSession, deleteAllSessions, newSessionId, saveSession, loadSessionsFromDb, deleteSessionFromDb, deleteAllSessionsFromDb, renameSessionInDb } from '../../services/aideySessionsService';
import { messageFromError } from '../../utils/apiError';   // Block 233
import { showErrorToast } from '../../utils/toast';        // Block 233
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { isEmailConnected } from '../../utils/emailConnected';
import { formatTaskClock } from '../../utils/taskTime';   // the one time formatter
import { chatTag } from '../../utils/chatAreas';           // C-b: the row's tag, from what the chat used
import { Button, Chip, SearchInput, Input, ConfirmDialog, OverflowMenu, useBreakpoint, v } from '../../ui';
import { useKeyboardViewport } from '../../ui/Overlay';
import './chat.css';

const PROJECT_ICONS  = ['💬', '📋', '💼', '📧', '💡', '🎯', '📊', '🔧', '🤝', '📝', '🚀', '⭐'];
// New projects store a token name; projects made before Block C keep the colour they were saved with.
const PROJECT_COLORS = ['pastel-periwinkle', 'pastel-mint', 'pastel-orange', 'pastel-lilac', 'pastel-pink', 'pastel-blue'];
const EXPAND_KEY = 'aidey-expand-session';   // written by the popup's Expand (App.js)

function projectColor(c) {
  if (!c) return v('pastel-neutral');
  if (String(c).startsWith('#')) return c;   // a colour saved by the old picker
  try { return v(c); } catch { return v('pastel-neutral'); }
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function sessionTime(s) { return new Date(s.updatedAt || s.createdAt); }

function startOfToday() { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }

// A row's time label: the time today, the day this week, else the date.
function groupLabel(d) {
  const today = startOfToday();
  if (d >= today) return 'Today';
  if (d >= new Date(today.getTime() - 6 * 86400000)) return 'This week';
  return 'Earlier';
}

// Decision 85: chats in no project, under Today and Earlier.
function groupUncategorised(sessions) {
  const today = startOfToday();
  const t = [], e = [];
  for (const s of sessions) (sessionTime(s) >= today ? t : e).push(s);
  return [['Today', t], ['Earlier', e]].filter(([, list]) => list.length);
}

// Today: the time. This week: the day. Earlier: the date.
function rowTime(s) {
  const d = sessionTime(s);
  if (isNaN(d.getTime())) return '';
  const label = groupLabel(d);
  if (label === 'Today') return formatTaskClock(d);
  if (label === 'This week') return d.toLocaleDateString('en-GB', { weekday: 'short' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

// Newer copy wins, by id; sessions only this browser has are added.
function mergeLocalInto(list, localSessions) {
  const byId = new Map(list.map(s => [s.id, s]));
  for (const l of localSessions) {
    const cur = byId.get(l.id);
    const t = (s) => new Date(s.updatedAt || s.createdAt || 0).getTime();
    if (!cur || t(l) > t(cur)) byId.set(l.id, { ...cur, ...l });
  }
  return [...byId.values()].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
}

function mergeSessions(dbSessions, localSessions) {
  const dbIds = new Set(dbSessions.map(s => s.id));
  const localOnly = localSessions.filter(s => !dbIds.has(s.id));
  return [...dbSessions, ...localOnly].sort((a, b) =>
    new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
  );
}

// The popup's Expand leaves the session it was on; read it once.
function takeExpandSession() {
  try {
    const id = sessionStorage.getItem(EXPAND_KEY);
    if (id) sessionStorage.removeItem(EXPAND_KEY);
    return id || null;
  } catch { return null; }
}

function listWords(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function AideyChatDashboard({ onCreateTask, userSchedule, onMoveTask, onCancelTask, activeWorkspaceId, onClose }) {
  useAutoTour('aidey-chat');
  const { currentUser, workspaces } = useCurrentUser();
  const breakpoint = useBreakpoint();
  const phone = breakpoint === 'phone';
  // On a phone or tablet the chat fills the screen, so it carries its own close (2026-10-08: on
  // Carla's iPhone the top bar showed only the menu, the page name and the bell).
  const showClose = breakpoint !== 'desktop' && typeof onClose === 'function';
  const rootRef = useRef(null);
  useKeyboardViewport(rootRef, phone);
  // Tablet and desktop: the card fills the rest of the viewport. Where it starts depends on the
  // shell and the header above it, so it is measured (document position, so a scrolled page does not
  // shrink it) and handed to the CSS as --achat-top.
  useEffect(() => {
    if (phone) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    const measure = () => {
      const card = root.querySelector('.achat-card');
      if (card) root.style.setProperty('--achat-top', `${Math.round(card.getBoundingClientRect().top + window.scrollY)}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(root.querySelector('.achat-head') || root);
    return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
  }, [phone]);

  const [sessions, setSessions]           = useState(() => loadSessions());
  const [projects, setProjects]           = useState([]);
  // Decision 85: which project headings are folded, remembered per person (their preferences on
  // the server, so it follows them to another device). { [projectId]: true } = folded.
  const [folded, setFolded] = useState(() => {
    const f = currentUser?.preferences?.chatFolds;
    return f && typeof f === 'object' && !Array.isArray(f) ? f : {};
  });
  // Saves go one after another: the server reads then writes preferences, so two saves in flight
  // at once (fold, then unfold, quickly) could land in the wrong order and leave it folded.
  const foldSaves = useRef(Promise.resolve());
  const toggleFold = useCallback((id) => {
    setFolded(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id]; else next[id] = true;
      foldSaves.current = foldSaves.current
        .then(() => api('/api/users/me', { method: 'PUT', body: JSON.stringify({ preferences: { chatFolds: next } }) }))
        .catch(err => console.error('[chat] fold state not saved', err?.message));
      return next;
    });
  }, []);
  const [activeId, setActiveId]           = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(() => newSessionId());
  const [renamingId, setRenamingId]       = useState(null);
  const [renameValue, setRenameValue]     = useState('');
  const [query, setQuery]                 = useState('');
  const [phoneView, setPhoneView]         = useState('chat'); // phone only: 'chat' | 'list'
  const [confirm, setConfirm]             = useState(null);   // { kind: 'chat' | 'all' | 'project', id?, name? }
  const renameInputRef                    = useRef(null);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [newProjectName, setNewProjectName]   = useState('');
  const [newProjectIcon, setNewProjectIcon]   = useState('💬');
  const [newProjectColor, setNewProjectColor] = useState(PROJECT_COLORS[0]);
  const [savingProject, setSavingProject]     = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await api('/api/aidey-projects');
      if (res.ok) { const d = await res.json(); setProjects(d.projects || []); }
    } catch {}
  }, []);

  // The server's list of chats: loading | ok | error. Only a list that LOADED may say "Start a
  // conversation"; a failed load says "Couldn't load your chats" with Retry (2026-10-08, live: a 401
  // drew the empty state over a server full of chats). Fetched once per workspace, and on Retry.
  const [historyLoad, setHistoryLoad] = useState({ status: 'loading', error: null });
  const historySeq = useRef(0);
  const loadHistory = useCallback((expanded = null) => {
    const seq = ++historySeq.current;   // a workspace switch mid-load must not apply the old list
    setHistoryLoad({ status: 'loading', error: null });
    loadSessionsFromDb().then(dbSessions => {
      if (seq !== historySeq.current) return;
      setHistoryLoad({ status: 'ok', error: null });
      if (!dbSessions.length) return;
      const merged = mergeSessions(dbSessions, loadSessions());
      setSessions(merged);
      if (expanded) return;
      const recent = merged[0];
      if (recent) {
        const diffH = (Date.now() - new Date(recent.updatedAt || recent.createdAt).getTime()) / 3600000;
        if (diffH < 24) { setActiveId(recent.id); setActiveSessionId(recent.id); }
      }
    }).catch(err => {
      if (seq !== historySeq.current) return;
      setHistoryLoad({ status: 'error', error: messageFromError(err, 'The server did not answer.') });
    });
  }, []);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    // The popup's Expand: open on that conversation (its messages are already in this browser).
    const expandId = takeExpandSession();
    const expanded = expandId && loadSession(expandId) ? expandId : null;
    // Clear previous workspace's sessions immediately so stale data never shows
    setSessions(expanded ? loadSessions() : []);
    setActiveId(expanded);
    setActiveSessionId(expanded || newSessionId());
    loadHistory(expanded);
    fetchProjects();
  }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // After a real change (the chat saved something new), fold this browser's copy into the list.
  // No request: the list is fetched once per workspace (it was re-fetched on every message render,
  // about once a second live).
  const handleSessionUpdated = useCallback(() => {
    setSessions(prev => mergeLocalInto(prev, loadSessions()));
  }, []);

  function startNewChat() {
    setActiveId(null);
    setActiveSessionId(newSessionId());
    setPhoneView('chat');
  }

  function openSession(id) {
    setActiveId(id);
    setActiveSessionId(id);
    setPhoneView('chat');
  }

  function deleteChat(id) {
    deleteSession(id);
    deleteSessionFromDb(id);
    setSessions(prev => prev.filter(s => s.id !== id));
    if (activeId === id) startNewChat();
  }

  function clearAll() {
    deleteAllSessions();
    deleteAllSessionsFromDb();
    setSessions([]);
    startNewChat();
  }

  function startRename(session) {
    setRenamingId(session.id);
    setRenameValue(session.title || '');
    setTimeout(() => renameInputRef.current?.focus(), 0);
  }

  function commitRename(session) {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== session.title) {
      saveSession({ ...session, title: trimmed });
      renameSessionInDb(session.id, trimmed);
      setSessions(prev => prev.map(s => s.id === session.id ? { ...s, title: trimmed } : s));
    }
    setRenamingId(null);
  }

  async function handleAssignProject(sessionId, newProjectId, currentProjectId) {
    try {
      if (newProjectId) {
        await api(`/api/aidey-projects/${newProjectId}/sessions/${sessionId}`, { method: 'POST' });
      } else if (currentProjectId) {
        await api(`/api/aidey-projects/${currentProjectId}/sessions/${sessionId}`, { method: 'DELETE' });
      }
      setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, projectId: newProjectId || null } : s));
      fetchProjects();
    } catch (err) { showErrorToast(messageFromError(err, "Could not assign project.")); }
  }

  async function handleCreateProject(e) {
    e.preventDefault();
    if (!newProjectName.trim()) return;
    setSavingProject(true);
    try {
      const res = await api('/api/aidey-projects', {
        method: 'POST',
        body: JSON.stringify({ name: newProjectName.trim(), icon: newProjectIcon, color: newProjectColor }),
      });
      if (res.ok) {
        const d = await res.json();
        setProjects(prev => [{ ...d.project, sessionCount: 0 }, ...prev]);
        setShowNewProjectForm(false);
        setNewProjectName('');
        setNewProjectIcon('💬');
        setNewProjectColor(PROJECT_COLORS[0]);
      }
    } catch (err) { showErrorToast(messageFromError(err, "Could not create project.")); }
    setSavingProject(false);
  }

  // Throws on failure so the ConfirmDialog shows the reason and stays open.
  async function deleteProject(projectId) {
    try {
      await api(`/api/aidey-projects/${projectId}`, { method: 'DELETE' });
    } catch (err) { throw new Error(messageFromError(err, "Could not delete project.")); }
    setProjects(prev => prev.filter(p => p.id !== projectId));
    setSessions(prev => prev.map(s => s.projectId === projectId ? { ...s, projectId: null } : s));
  }

  const q = query.trim().toLowerCase();
  // Search runs across every group.
  const filteredSessions = sessions.filter(s => !q || String(s.title || 'Untitled').toLowerCase().includes(q));
  const currentTitle = activeId ? (sessions.find(s => s.id === activeId)?.title || 'Chat') : 'New chat';
  const projectMap = Object.fromEntries(projects.map(p => [p.id, p]));
  // Decision 85: a heading per project that has at least one chat (none for an empty one), then the
  // chats in no project by date. A chat moved into a project is under its heading at once (the
  // list is derived from projectId, which handleAssignProject sets before anything is fetched).
  const projectSections = projects
    .map(p => ({ project: p, chats: filteredSessions.filter(s => s.projectId === p.id) }))
    .filter(sec => sec.chats.length > 0);
  const known = new Set(projects.map(p => p.id));
  const uncategorised = groupUncategorised(filteredSessions.filter(s => !s.projectId || !known.has(s.projectId)));

  // What Aidey can read here: its own tasks and calendar, invoices in a business, email when one
  // is connected. Read from data already loaded; nothing is fetched for it.
  const activeWorkspace = (workspaces || []).find(w => w.id === activeWorkspaceId) || null;
  const isPersonal = activeWorkspace?.type === 'personal';
  const connected = ['tasks', 'calendar', ...(isPersonal ? [] : ['invoices']), ...(isEmailConnected(currentUser) ? ['email'] : [])];

  const showList = !phone || phoneView === 'list';
  const showChat = !phone || phoneView === 'chat';

  // One history row (decision 85: a row under its project's heading does not repeat the project).
  function renderRow(session, { inProject = false } = {}) {
                  const isActive   = session.id === activeId;
                  const isRenaming = session.id === renamingId;
                  const isOld = (Date.now() - sessionTime(session).getTime()) > 30 * 86400000;
                  const proj  = session.projectId ? projectMap[session.projectId] : null;
                  const showProj = proj && !inProject;
                  const tag   = chatTag(session);
                  const menu = [
                    { label: 'Rename', onSelect: () => startRename(session) },
                    ...projects.filter(p => p.id !== session.projectId).map(p => ({
                      label: `Move to ${`${p.icon || ''} ${p.name}`.trim()}`,
                      onSelect: () => handleAssignProject(session.id, p.id, session.projectId),
                    })),
                    ...(session.projectId ? [{ label: 'Remove from project', onSelect: () => handleAssignProject(session.id, null, session.projectId) }] : []),
                    { label: 'Delete chat', danger: true, onSelect: () => setConfirm({ kind: 'chat', id: session.id, name: session.title }) },
                  ];

                  return (
                    <div key={session.id} className={`achat-hrow${isActive ? ' is-active' : ''}${isOld ? ' is-old' : ''}`}>
                      <span className="achat-hrow__stripe" style={{ '--ui-bg': isActive ? v('highlight') : projectColor(proj?.color) }} aria-hidden="true" />
                      {isRenaming ? (
                        <div className="achat-hrow__open">
                          <Input
                            ref={renameInputRef}
                            className="achat-hrow__rename"
                            aria-label="Chat name"
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onBlur={() => commitRename(session)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitRename(session); } if (e.key === 'Escape') setRenamingId(null); }}
                          />
                        </div>
                      ) : (
                        <button type="button" className="achat-hrow__open" aria-current={isActive ? 'true' : undefined} onClick={() => openSession(session.id)}>
                          <span className="achat-hrow__title">{session.title || 'Untitled'}</span>
                          <span className="achat-hrow__meta">
                            <span className="achat-hrow__tags">
                              {tag && <span className="achat-hrow__area" data-testid="chat-row-tag" style={{ '--ui-bg': v(tag.color) }}>{tag.label}</span>}
                              {showProj && <span className="achat-hrow__tag">{proj.icon} {proj.name}</span>}
                            </span>
                            <span className="achat-hrow__time">{rowTime(session)}</span>
                          </span>
                        </button>
                      )}
                      {!isRenaming && (
                        <span className="achat-hrow__more">
                          <OverflowMenu label={`More for ${session.title || 'this chat'}`} items={menu} />
                        </span>
                      )}
                    </div>
                  );
  }

  const confirmCopy = confirm?.kind === 'all'
    ? { title: 'Delete all chat history?', message: 'Every Aidey chat in this workspace will be deleted. This cannot be undone.', label: 'Delete all' }
    : confirm?.kind === 'project'
      ? { title: 'Delete this project?', message: `"${confirm.name}" will be deleted. Your chats will not be deleted.`, label: 'Delete project' }
      : { title: 'Delete this chat?', message: `"${confirm?.name || 'Untitled'}" will be deleted. This cannot be undone.`, label: 'Delete chat' };

  return (
    <div ref={rootRef} className={`achat${phone ? ' achat--phone' : ''}`}>
      <header className="achat-head">
        <div className="achat-head__text">
          <span className="ui-eyebrow">
            <span className="ui-eyebrow__dot" style={{ '--ui-bg': v('focus') }} aria-hidden="true" />
            Your AI exec assistant
          </span>
          <span className="achat-head__titlerow">
            <h1 className="ui-h1">Aidey</h1>
            <span className="ui-page-header__help"><HelpButton tourId="aidey-chat" /></span>
          </span>
        </div>
        <div className="achat-head__actions">
          {phone && (phoneView === 'chat'
            ? <Button variant="secondary" size="sm" onClick={() => setPhoneView('list')}>Chats</Button>
            : <Button variant="secondary" size="sm" onClick={() => setPhoneView('chat')}><span aria-hidden="true">←</span> Chat</Button>)}
          <Button variant="create" size={phone ? 'sm' : undefined} data-tour-id="chat-new-chat" onClick={startNewChat}>{phone ? '+ New' : '+ New chat'}</Button>
        </div>
      </header>

      <div className="achat-card">
        {/* ── History ─────────────────────────────────────────────── */}
        {/* Both stay mounted on a phone (hidden, not removed), so a reply in flight is never lost
            by looking at the list. */}
        <aside className="achat-side" data-tour-id="chat-history" aria-label="Your chats" hidden={!showList}>
            <SearchInput label="Search chats" placeholder="Search chats…" value={query} onChange={e => setQuery(e.target.value)} />

            <div className="achat-projbar">
              <Chip selected={showNewProjectForm} onClick={() => setShowNewProjectForm(f => !f)}>+ Project</Chip>
            </div>

            {showNewProjectForm && (
              <form className="achat-newproj" onSubmit={handleCreateProject}>
                <Input
                  value={newProjectName}
                  onChange={e => setNewProjectName(e.target.value)}
                  placeholder="Project name"
                  aria-label="Project name"
                  maxLength={50}
                  autoFocus
                />
                <div className="achat-newproj__row" role="group" aria-label="Icon">
                  {PROJECT_ICONS.map(ic => (
                    <button key={ic} type="button" className="achat-newproj__icon" aria-pressed={newProjectIcon === ic} onClick={() => setNewProjectIcon(ic)}>{ic}</button>
                  ))}
                </div>
                <div className="achat-newproj__row" role="group" aria-label="Colour">
                  {PROJECT_COLORS.map(c => (
                    <button key={c} type="button" className="achat-newproj__swatch" aria-pressed={newProjectColor === c}
                      aria-label={c.replace('pastel-', '')} style={{ '--ui-bg': v(c) }} onClick={() => setNewProjectColor(c)} />
                  ))}
                </div>
                <div className="achat-newproj__actions">
                  <Button variant="tertiary" size="sm" onClick={() => { setShowNewProjectForm(false); setNewProjectName(''); }}>Cancel</Button>
                  <Button type="submit" variant="confirm" size="sm" disabled={!newProjectName.trim() || savingProject}>
                    {savingProject ? 'Creating…' : 'Create'}
                  </Button>
                </div>
              </form>
            )}

            {historyLoad.status === 'error' && (
              <div className="achat-loadfail achat-loadfail--side" role="alert" data-testid="chat-history-failed">
                <strong>Couldn't load your chats.</strong>
                <span>{historyLoad.error} Your chats are still on the server; this is not an empty list.</span>
                <Button variant="secondary" size="sm" onClick={() => loadHistory()}>Retry</Button>
              </div>
            )}
            {historyLoad.status === 'loading' && filteredSessions.length === 0 && (
              <p className="achat-side__empty" role="status">Loading your chats…</p>
            )}

            {historyLoad.status === 'ok' && filteredSessions.length === 0 && (
              <p className="achat-side__empty">
                {q
                  ? 'No chats match that search.'
                  : 'Start a conversation and it will appear here, so you can pick it up later.'}
              </p>
            )}

            {projectSections.map(({ project: p, chats }) => {
              const open = q ? true : !folded[p.id];   // a search opens every group it found something in
              return (
                <div key={p.id} className="achat-group achat-group--project" data-testid="chat-project-group">
                  <div className="achat-group__headrow">
                    <button type="button" className="achat-group__label achat-group__toggle" aria-expanded={open}
                      onClick={() => toggleFold(p.id)} disabled={!!q}>
                      <span className="achat-group__chev" aria-hidden="true">{open ? '▾' : '▸'}</span>
                      <span className="achat-group__name">{`${p.icon || ''} ${p.name}`.trim()}</span>
                      <span className="achat-group__count" aria-label={`${chats.length} chat${chats.length === 1 ? '' : 's'}`}>{chats.length}</span>
                    </button>
                    <OverflowMenu label={`More for ${p.name}`} items={[{ label: 'Delete project', danger: true, onSelect: () => setConfirm({ kind: 'project', id: p.id, name: p.name }) }]} />
                  </div>
                  {open && chats.map(session => renderRow(session, { inProject: true }))}
                </div>
              );
            })}

            {uncategorised.map(([label, groupItems]) => (
              <div key={label} className="achat-group">
                <span className="achat-group__label">{label}</span>
                {groupItems.map(session => renderRow(session))}
              </div>
            ))}

            {sessions.length > 0 && (
              <div className="achat-side__foot">
                <Button variant="destructive" size="sm" block onClick={() => setConfirm({ kind: 'all' })}>Clear all history</Button>
              </div>
            )}
        </aside>

        {/* ── Conversation ────────────────────────────────────────── */}
        <section className="achat-main" aria-label="Conversation" hidden={!showChat}>
            <div className="achat-main__head">
              <div className="achat-main__who">
                <span className="achat-av achat-av--head" aria-hidden="true">A</span>
                <div className="achat-main__titles">
                  <span className="achat-main__title">{currentTitle}</span>
                  <span className="achat-main__sub">
                    <span className="achat-dot" aria-hidden="true" />
                    <span>Connected to {listWords(connected)}</span>
                  </span>
                </div>
              </div>
              {showClose && (
                <button type="button" className="achat-pageclose ui-hit" onClick={onClose} aria-label="Close chat" title="Close" data-testid="chat-page-close">
                  <span aria-hidden="true">✕</span>
                </button>
              )}
            </div>
            <div className="achat-main__body" data-tour-id="chat-messages">
              <SuperAideyChat
                key={activeSessionId}
                onCreateTask={onCreateTask}
                userSchedule={userSchedule}
                onMoveTask={onMoveTask}
                onCancelTask={onCancelTask}
                isPopup={false}
                fullPage={true}
                sessionId={activeSessionId}
                fromHistory={!!activeId}
                onSessionUpdated={handleSessionUpdated}
              />
            </div>
        </section>
      </div>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirmCopy.title}
        message={confirmCopy.message}
        confirmLabel={confirmCopy.label}
        onConfirm={async () => {
          if (confirm?.kind === 'all') clearAll();
          else if (confirm?.kind === 'project') await deleteProject(confirm.id);
          else if (confirm?.kind === 'chat') deleteChat(confirm.id);
        }}
      />
    </div>
  );
}
